import { Link } from '@fountain/fountain-ui-components';

import theme from 'theme';
import { Grid, Typography } from '@material-ui/core';
import {
  CancelablePromise,
  EventAvailableSlot,
  EventFormResult,
  EventsService,
} from 'api-clients/monolith';
import { useForm } from 'hooks';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import GlobalNav from 'components/GlobalNav';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import { usePostHog } from 'hooks/usePostHog';
import LeftArrow from 'images/arrow-left.svg';
import InfoIcon from 'images/InfoIcon';
import globalMessages from 'shared/global/messages';

import analyticsEvents from './analyticsEvents';
import { defaultEventData } from './constants';
import { EventDetails } from './EventDetails';
import { EventSettings } from './EventSettings';
import { Footer } from './Footer';
import { LocationDetails } from './LocationDetails/LocationDetails';
import { messages } from './messages';
import { StageSelector } from './StageSelector/StageSelector';
import { useStyles } from './styles';
import { AnalyticsEventType, CreateEventProps, UpdateModeType } from './types';
import { initZone } from './utils';

interface RouteParams {
  accountSlug: string;
}

export const CreateEvent: VFC<CreateEventProps> = ({ event, isUpdate }) => {
  const styles = useStyles();
  const { accountSlug } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const intl = useIntl();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const returnUrl = urlSearchParams.get('returnUrl');
  const series = !!urlSearchParams.get('editSeries');
  const warning = !series && isUpdate && !!event.series_key;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const updateModeRef = useRef<UpdateModeType>('single');
  const submittedValuesRef = useRef<EventAvailableSlot | null>(null);
  const pendingEventDateRef = useRef<string | undefined>();

  const calendarPageUrl = `${REACT_APP_MONOLITH_BASE_URL}/${accountSlug}/calendar`;

  const isValidReturnUrl = (url: string | null): url is string =>
    !!url && url.startsWith(`/${accountSlug}/`) && !url.includes('..');

  const getBackUrl = () => {
    if (isValidReturnUrl(returnUrl)) {
      return `${REACT_APP_MONOLITH_BASE_URL}${returnUrl}`;
    }
    return calendarPageUrl;
  };

  const getSaveReturnUrl = (eventDate?: string) => {
    if (isValidReturnUrl(returnUrl)) {
      const [path, query] = returnUrl.split('?');
      const params = new URLSearchParams(query || '');
      if (eventDate) {
        params.set('date', eventDate);
      }
      const queryString = params.toString();
      return `${REACT_APP_MONOLITH_BASE_URL}${path}${
        queryString ? `?${queryString}` : ''
      }`;
    }
    return calendarPageUrl;
  };

  const { event_adaptive_enabled: adaptiveEvents, time_zone: zone } =
    useSelector(makeSelectWhoami());

  const isDateTodayOrFuture = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const givenDate = new Date(dateString);
    givenDate.setHours(0, 0, 0, 0);

    return givenDate >= today;
  };

  const validate = (values: Partial<EventAvailableSlot>) => {
    const errors: Partial<Record<keyof EventAvailableSlot, string>> = {};

    if (values.stage_titles && values.stage_titles.length === 0) {
      errors.stage_titles = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (values.opening_titles && values.opening_titles.length === 0) {
      errors.opening_titles = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (
      values.location_group_titles &&
      values.location_group_titles.length === 0
    ) {
      errors.location_group_titles = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (!values.max_attendees) {
      errors.max_attendees = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (!values.user_external_id) {
      errors.user_external_id = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (!values.start_time) {
      errors.start_time = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (values.start_time && !isDateTodayOrFuture(values.start_time)) {
      errors.start_time = intl.formatMessage(messages.timeErrorMessage);
    }
    if (!values.end_time) {
      errors.end_time = intl.formatMessage(globalMessages.requiredFieldMessage);
    }
    if (values.end_time && !isDateTodayOrFuture(values.end_time)) {
      errors.end_time = intl.formatMessage(messages.timeErrorMessage);
    }
    if (values.period && !values.frequency) {
      errors.frequency = intl.formatMessage(
        globalMessages.requiredFieldMessage,
      );
    }
    if (Object.keys(errors).length > 0) {
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.changesFailedToSave),
          'error',
        ),
      );
    }
    return errors;
  };

  const redirectToCalendar = (eventDate?: string) => {
    window.location.href = eventDate
      ? getSaveReturnUrl(eventDate)
      : getBackUrl();
  };

  const sendTrackingEvent = (
    eventName: AnalyticsEventType,
    result: EventFormResult,
  ) => {
    const isUpdate = eventName === analyticsEvents.AVAILABILITY_SLOT_UPDATE;
    const repeatSettings = {
      period: submittedValuesRef.current?.period || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
      frequency: submittedValuesRef.current?.frequency ?? null,
      split: submittedValuesRef.current?.split ?? null,
    };

    posthog.capture(eventName, {
      slot_id: result.external_id,
      slot_name: submittedValuesRef.current?.title ?? '',
      slots_count: result.count,
      event_instructions: submittedValuesRef.current?.instructions ?? '',
      max_attendees: Number(submittedValuesRef.current?.max_attendees),
      new_calendar: true,
      update_mode: isUpdate ? updateModeRef.current : undefined,
      repeat: Object.values(repeatSettings).every(v => v === null)
        ? null
        : repeatSettings,
    });
  };

  const { mutation: createEvent } = useApiServiceMutation<
    EventFormResult,
    ({
      // eslint-disable-next-line camelcase
      user_external_id,
      event,
    }: {
      // eslint-disable-next-line camelcase
      user_external_id: string;
      event: EventAvailableSlot;
    }) => CancelablePromise<EventFormResult>,
    { errors?: string[] }
    // eslint-disable-next-line @typescript-eslint/unbound-method
  >(EventsService.postInternalApiEvents, {
    onSuccess: result => {
      setIsLoading(false);
      dispatch(
        addMessageAction(
          intl.formatMessage(messages[result.key], {
            count: result.count,
          }),
          'success',
        ),
      );

      sendTrackingEvent(analyticsEvents.AVAILABILITY_SLOT_CREATE, result);
      redirectToCalendar(pendingEventDateRef.current);
    },
    onError: error => {
      setIsSaveButtonEnabled(false);
      setIsLoading(false);
      const serverError = error?.errors?.join(' ');
      dispatch(
        addMessageAction(
          serverError || intl.formatMessage(messages.eventCreateFailed),
          'error',
        ),
      );
    },
  });

  const { mutation: updateEvent } = useApiServiceMutation<
    EventFormResult,
    (
      externalId: string,
      {
        // eslint-disable-next-line camelcase
        user_external_id,
        // eslint-disable-next-line camelcase
        update_mode,
        event,
      }: {
        // eslint-disable-next-line camelcase
        user_external_id: string;
        // eslint-disable-next-line camelcase
        update_mode: 'single' | 'unbooked' | 'all' | undefined;
        event: EventAvailableSlot;
      },
    ) => CancelablePromise<EventFormResult>
    // eslint-disable-next-line @typescript-eslint/unbound-method
  >(EventsService.putInternalApiEvents, {
    onSuccess: result => {
      setIsLoading(false);
      dispatch(
        addMessageAction(
          intl.formatMessage(messages[result.key], {
            count: result.count,
          }),
          'success',
        ),
      );

      sendTrackingEvent(analyticsEvents.AVAILABILITY_SLOT_UPDATE, result);
      redirectToCalendar(pendingEventDateRef.current);
    },
    onError: () => {
      setIsSaveButtonEnabled(false);
      setIsLoading(false);
      const message = series
        ? messages.seriesEditedFailed
        : messages.sessionEditedFailed;
      dispatch(addMessageAction(intl.formatMessage(message), 'error'));
    },
  });

  const onSubmit = (values: EventAvailableSlot) => {
    setIsSaveButtonEnabled(false);
    setIsLoading(true);
    submittedValuesRef.current = values;
    pendingEventDateRef.current = values.start_time?.split('T')[0];

    if (isUpdate && values.external_id) {
      updateEvent(values.external_id, {
        user_external_id: values.user ?? '',
        update_mode: updateModeRef.current,
        event: values,
      });
    } else {
      createEvent({
        user_external_id: values.user ?? '',
        event: values,
      });
    }
  };

  const { handleChange, values, errors, runValidations } =
    useForm<EventAvailableSlot>(onSubmit, validate, event ?? defaultEventData);

  const onChange = useCallback((params: Partial<EventAvailableSlot>) => {
    setIsSaveButtonEnabled(true);
    handleChange(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    redirectToCalendar();
  };

  const handleFooterSubmit = (mode: UpdateModeType) => {
    updateModeRef.current = mode;
    onSubmit(values);
  };

  const title = isUpdate
    ? series
      ? messages.updateSeries
      : messages.updateEvent
    : messages.createEvent;
  const formattedTitle = intl.formatMessage(title);

  useEffect(() => {
    initZone(zone);
  }, [zone]);

  return (
    <>
      <Helmet>
        <title>{formattedTitle}</title>
        <meta name="description" content={formattedTitle} />
      </Helmet>

      <Grid container direction="column" className={styles.createEventWrapper}>
        <GlobalNav />

        <Grid
          container
          direction="column"
          className={styles.createEventContainer}
        >
          <Grid item className={styles.backToSettings}>
            <Link color="secondary" href={getBackUrl()}>
              <img
                src={LeftArrow}
                alt="back arrow"
                className={styles.leftArrow}
              />
              <FormattedMessage {...globalMessages.back} />
            </Link>
          </Grid>

          <Grid
            item
            container
            direction="column"
            className={styles.createEventContent}
          >
            {warning && (
              <>
                <Grid className={styles.warningContent}>
                  <InfoIcon
                    color={theme.palette.common.infoMark}
                    className={styles.warningIcon}
                  />
                  <Typography variant="h5" className={styles.warningText}>
                    <FormattedMessage {...messages.eject} />
                  </Typography>
                </Grid>
              </>
            )}
            <Typography display="inline" variant="h2">
              {formattedTitle}
            </Typography>
            <Typography
              variant="body2"
              color="textPrimary"
              className={styles.subtitle}
            >
              <FormattedMessage {...messages.subTitle} />
            </Typography>
            <form>
              <EventDetails handleChange={onChange} availableSlot={values} />
              <EventSettings
                handleChange={onChange}
                availableSlot={values}
                errors={errors}
                series={series}
                adaptiveEvents={adaptiveEvents}
              />

              <StageSelector
                handleChange={onChange}
                errors={errors}
                availableSlot={values}
              />
              <LocationDetails handleChange={onChange} availableSlot={values} />
              <Footer
                isUpdatingEvent={isLoading}
                isSaveButtonEnabled={isSaveButtonEnabled}
                resetForm={handleCancel}
                redirectToCalendar={redirectToCalendar}
                availableSlot={values}
                onSubmit={mode => handleFooterSubmit(mode)}
                runValidations={runValidations}
              ></Footer>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
